import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, KeyRound, ShieldCheck, Pause, Play, Trash2, RotateCcw, Cake, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { RecordedSave } from "@/admin/Recorded";
import { useAuth } from "@/context/AuthContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { Field, inputCls, PasswordInput } from "@/components/common";

const fmt = (d?: string) => d ? new Date(d).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
const FILTERS = [["all", "All"], ["admin", "Admins"], ["customer", "Customers"], ["paused", "Paused"], ["trashed", "Trash"]];

const Act = ({ icon: Icon, children, tone = "line", ...p }: any) => (
  <button {...p} className={`inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors ${
    tone === "dark" ? "bg-neutral-900 text-white hover:bg-[var(--c-primary)]" : tone === "danger" ? "bg-[#7A2420] text-white hover:bg-[#5e1a17]" : "border border-neutral-300 bg-white hover:border-neutral-900"}`}>
    <Icon size={14} strokeWidth={1.5} />{children}
  </button>
);

function UserRow({ u, me, meMaster, run, onEdit, onPw }: any) {
  const trashed = u.status === "trashed";
  const locked = u.is_master;
  const canManage = !locked || meMaster;
  return (
    <div className="py-7 border-b border-neutral-200 flex flex-col lg:flex-row lg:items-start gap-5 justify-between" data-testid={`user-row-${u.email}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-3xl">{u.name}</h2>
          <span className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 ${u.status === "active" ? "bg-[#DDE5D0] text-[#33421F]" : u.status === "paused" ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800"}`} data-testid="user-status-badge">{u.status}</span>
          {u.role === "admin" && <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1 bg-neutral-900 text-white" data-testid="user-admin-badge">{u.is_master ? "Master Admin" : "Admin"}</span>}
          {u.id === me && <span className="text-[10px] text-neutral-500">(you)</span>}
        </div>
        <a href={`mailto:${u.email}`} className="block mt-2 text-[var(--c-primary)] underline underline-offset-4">{u.email}</a>
        <p className="mt-2 text-sm text-neutral-700 flex flex-wrap items-center gap-x-2">
          {u.phone && <a href={`tel:${u.phone}`} className="underline underline-offset-4">{u.phone}</a>}
          {u.phone && u.location && <span>·</span>}{u.location && <span>{u.location}</span>}
          {u.birthday && <span className="inline-flex items-center gap-1 ml-2"><Cake size={13} strokeWidth={1.4} />{u.birthday}</span>}
        </p>
        <p className="mt-2 text-xs text-neutral-500" data-testid="user-session-info">
          {u.sessions || 0} sessions · {u.last_login_at ? `Last login ${fmt(u.last_login_at)}${u.last_login_ip ? ` · IP ${u.last_login_ip}` : ""}` : "Never logged in"} · Joined {fmt(u.created_at).split(",")[0]}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 lg:justify-end">
        {trashed ? (
          <>
            <Act icon={RotateCcw} onClick={() => run(u, "status", { status: "active" }, "User restored")} data-testid="user-restore-button">Restore</Act>
            <Act icon={Trash2} tone="danger" onClick={() => window.confirm(`Permanently delete ${u.name}? This can't be undone.`) && run(u, "delete", null, "User deleted")} data-testid="user-delete-forever-button">Delete forever</Act>
          </>
        ) : (
          <>
            {canManage && <Act icon={Pencil} onClick={() => onEdit(u)} data-testid="user-edit-button">Edit</Act>}
            {canManage && <Act icon={KeyRound} onClick={() => onPw(u)} data-testid="user-reset-pw-button">Reset PW</Act>}
            {locked && <span className="text-[11px] text-neutral-500 self-center" data-testid="user-master-locked">Protected account</span>}
            {u.id !== me && !locked && (u.role === "admin"
              ? <Act icon={ShieldCheck} tone="dark" onClick={() => run(u, "role", { role: "customer" }, "Admin access revoked")} data-testid="user-revoke-admin-button">Revoke Admin</Act>
              : <Act icon={ShieldCheck} onClick={() => run(u, "role", { role: "admin" }, "Admin access granted")} data-testid="user-grant-admin-button">Grant Admin</Act>)}
            {u.id !== me && !locked && (u.status === "paused"
              ? <Act icon={Play} onClick={() => run(u, "status", { status: "active" }, "Account resumed")} data-testid="user-resume-button">Resume</Act>
              : <Act icon={Pause} onClick={() => run(u, "status", { status: "paused" }, "Account paused")} data-testid="user-pause-button">Pause</Act>)}
            {u.id !== me && !locked && <Act icon={Trash2} tone="danger" onClick={() => window.confirm(`Move ${u.name} to trash? They won't be able to sign in.`) && run(u, "status", { status: "trashed" }, "Moved to trash")} data-testid="user-trash-button">Trash</Act>}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<any>(null);
  const [pw, setPw] = useState<any>(null);
  const load = () => api.get("/admin-x/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const run = async (u: any, action: string, body: any, msg: string) => {
    try {
      if (action === "delete") await api.delete(`/admin-x/users/${u.id}`); else await api.post(`/admin-x/users/${u.id}/${action}`, body);
      toast(msg); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveEdit = async () => {
    try {
      if (edit.id) await api.put(`/admin-x/users/${edit.id}`, edit); else await api.post("/admin-x/users", edit);
      toast(edit.id ? "User updated" : "User created"); if (!edit.id) setEdit(null); load(); return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const savePw = async () => {
    try { await api.post(`/admin-x/users/${pw.id}/password`, { password: pw.password || null, send_link: pw.send_link }); toast(pw.send_link ? "Password updated / reset link emailed" : "Password updated"); setPw(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shown = users.filter((u) => {
    const st = u.status || "active";
    const ok = filter === "all" ? st !== "trashed" : filter === "paused" || filter === "trashed" ? st === filter : u.role === filter && st !== "trashed";
    return ok && (!q || `${u.name} ${u.email} ${u.phone || ""} ${u.location || ""}`.toLowerCase().includes(q.toLowerCase()));
  });
  const count = (k: string) => users.filter((u) => (k === "all" ? u.status !== "trashed" : k === "paused" || k === "trashed" ? u.status === k : u.role === k && u.status !== "trashed")).length;
  const set = (k: string, v: any) => setEdit({ ...edit, [k]: v });

  return (
    <div data-testid="admin-users">
      <AdminTitle title="Users" sub="Customers and staff accounts. Paused or trashed users can't sign in.">
        <input placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-neutral-300 bg-white px-3 py-2 text-sm" data-testid="users-search" />
        <Act icon={UserPlus} tone="dark" onClick={() => setEdit({ name: "", email: "", phone: "", location: "", birthday: "", password: "", role: "customer" })} data-testid="user-add-button">Add User</Act>
      </AdminTitle>
      <div className="flex flex-wrap gap-6 border-b border-neutral-200">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`pb-3 -mb-px border-b-2 text-base ${filter === k ? "border-[var(--c-primary)] text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`} data-testid={`users-filter-${k}`}>{l} ({count(k)})</button>
        ))}
      </div>
      {shown.length === 0 && <p className="py-16 text-sm text-neutral-500" data-testid="users-empty">No users here.</p>}
      {shown.map((u) => <UserRow key={u.id} u={u} me={user?.id} meMaster={!!users.find((x) => x.id === user?.id)?.is_master} run={run} onEdit={(x: any) => setEdit({ ...x })} onPw={(x: any) => setPw({ ...x, password: "", send_link: false })} />)}

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="user-edit-dialog">
          <DialogTitle className="font-display text-2xl">{edit?.id ? `Edit ${edit.name}` : "Add User"}</DialogTitle>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name"><input value={edit.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="user-form-name" /></Field>
              <Field label="Email"><input type="email" value={edit.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="user-form-email" /></Field>
              <Field label="Phone"><input value={edit.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="user-form-phone" /></Field>
              <Field label="Birthday"><input type="date" value={edit.birthday || ""} onChange={(e) => set("birthday", e.target.value)} className={inputCls} data-testid="user-form-birthday" /></Field>
              <div className="sm:col-span-2"><Field label="Location"><input value={edit.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Melbourne, Victoria (VIC)" className={inputCls} data-testid="user-form-location" /></Field></div>
              {!edit.id && (
                <>
                  <Field label="Password (min 8)"><PasswordInput value={edit.password} onChange={(e: any) => set("password", e.target.value)} testId="user-form-password" /></Field>
                  <Field label="Role"><select value={edit.role} onChange={(e) => set("role", e.target.value)} className={inputCls} data-testid="user-form-role"><option value="customer">Customer</option><option value="admin">Admin</option></select></Field>
                </>
              )}
              <div className="sm:col-span-2"><RecordedSave key={edit.id || "new"} res="users" id={edit.id} onSave={saveEdit} value={edit} testId="user-form-save" /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pw} onOpenChange={(v) => !v && setPw(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="user-password-dialog">
          <DialogTitle className="font-display text-2xl">Reset password · {pw?.name}</DialogTitle>
          {pw && (
            <div className="space-y-4">
              <Field label="New password (optional, min 8)"><PasswordInput value={pw.password} onChange={(e: any) => setPw({ ...pw, password: e.target.value })} testId="user-pw-input" /></Field>
              <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={pw.send_link} onChange={(e) => setPw({ ...pw, send_link: e.target.checked })} data-testid="user-pw-send-link" />Email {pw.email} a link to choose their own password</label>
              <Act icon={KeyRound} tone="dark" onClick={savePw} data-testid="user-pw-save">Update</Act>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
